import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SettingsRow, SettingsSection, Switch } from '@wundo/ui';
import { SaveStatus } from '@/components/SaveStatus.tsx';
import { FollowedColleaguesSection } from '@/components/settings/FollowedColleaguesSection.tsx';
import { MeetingExceptionsSection } from '@/components/settings/MeetingExceptionsSection.tsx';
import { MeetingKindsSection } from '@/components/settings/MeetingKindsSection.tsx';
import {
  fetchFollowExceptions,
  fetchFollowPreferences,
  removeFollowException,
  saveFollowPreferences,
  type MeetingFollowExceptionItem,
  type MeetingFollowPreferences,
} from '@/lib/meetingFollow.ts';
import { m } from '@/paraglide/messages.js';

export const Route = createFileRoute('/_app/settings/meetings')({
  component: MeetingSettingsPage,
});

export function MeetingSettingsPage() {
  const queryClient = useQueryClient();

  const {
    data: preferences,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['meetingFollowPreferences'],
    queryFn: fetchFollowPreferences,
  });

  const { data: exceptions } = useQuery({
    queryKey: ['meetingFollowExceptions'],
    queryFn: fetchFollowExceptions,
  });

  const save = useMutation({
    mutationFn: saveFollowPreferences,
    /* The server is what decides what was stored, so the page follows it. */
    onSuccess: (saved) => queryClient.setQueryData(['meetingFollowPreferences'], saved),
  });

  const remove = useMutation({
    mutationFn: removeFollowException,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['meetingFollowExceptions'] }),
  });

  if (isPending) return null;
  if (isError) {
    return <p className='text-base text-ink-muted'>{m.meetings_load_failed()}</p>;
  }

  /* Save the whole choice every time: one PUT, one stored record, no merging. */
  const change = (patch: Partial<MeetingFollowPreferences>) =>
    save.mutate({
      follow_one_on_ones: preferences.follow_one_on_ones,
      follow_team_meetings: preferences.follow_team_meetings,
      follow_all: preferences.follow_all,
      notetaker: preferences.notetaker,
      ...patch,
    });

  const followed = (exceptions ?? []).filter(isFollowedColleague);
  const switchedOff = (exceptions ?? []).filter(
    (exception) => !isFollowedColleague(exception),
  );

  return (
    <>
      <SaveStatus pending={save.isPending} failed={save.isError} />

      <MeetingKindsSection preferences={preferences} onChange={change} />

      <FollowedColleaguesSection
        covered={preferences.follow_one_on_ones}
        followed={followed}
        corrections={exceptions ?? []}
        removing={remove.isPending}
        onRemove={(exceptionId) => remove.mutate(exceptionId)}
      />

      <SettingsSection title={m.meetings_notetaker_title()}>
        <SettingsRow
          label={m.meetings_notetaker()}
          description={m.meetings_notetaker_desc()}
        >
          <Switch
            aria-label={m.meetings_notetaker()}
            checked={preferences.notetaker}
            onCheckedChange={(checked) => change({ notetaker: checked })}
          />
        </SettingsRow>
      </SettingsSection>

      <MeetingExceptionsSection
        exceptions={switchedOff}
        removing={remove.isPending}
        onRemove={(exceptionId) => remove.mutate(exceptionId)}
      />
    </>
  );
}

/* A colleague whose one-to-ones Wundo follows: a pick, not an exception. */
function isFollowedColleague(exception: MeetingFollowExceptionItem): boolean {
  return exception.target_kind === 'person' && exception.follow === true;
}
